import { useEffect, useState } from 'react';

interface Props {
  code: string;
  /** Compact layout for the host sidebar. */
  small?: boolean;
}

/**
 * The join QR. The PNG comes from the server (which knows the LAN IP), so the
 * code always points somewhere a phone can actually reach — not "localhost".
 *
 * On the TV the corner code is too small to scan from across the room, so
 * clicking it blows it up to fill the screen until it's dismissed.
 */
export function JoinQr({ code, small }: Props) {
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/sessions/${encodeURIComponent(code)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body: { joinUrl?: string } | null) => {
        if (!cancelled && body?.joinUrl) setJoinUrl(body.joinUrl);
      })
      .catch(() => {
        /* the code below is still readable without the URL */
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const qrSrc = `/api/sessions/${encodeURIComponent(code)}/qr`;
  const prettyUrl = joinUrl?.replace(/^https?:\/\//, '');

  return (
    <>
      <div className="tv-join">
        {small ? (
          <img src={qrSrc} alt={`QR code to join game ${code}`} style={{ width: '5rem', height: '5rem' }} />
        ) : (
          <button
            type="button"
            className="tv-join-trigger"
            onClick={() => setExpanded(true)}
            aria-label="Enlarge the join QR code"
          >
            <img src={qrSrc} alt={`QR code to join game ${code}`} />
          </button>
        )}
        <div>
          <div className="faint">Join with your phone</div>
          <div className="tv-join-code">{code}</div>
          {prettyUrl ? <div className="tv-join-url">{prettyUrl}</div> : null}
        </div>
      </div>

      {expanded ? (
        <div
          className="qr-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Join QR code"
          onClick={() => setExpanded(false)}
        >
          <div className="qr-full" onClick={(e) => e.stopPropagation()}>
            <img src={qrSrc} alt={`QR code to join game ${code}`} />
            <div className="qr-full-code">{code}</div>
            {prettyUrl ? <div className="qr-full-url">{prettyUrl}</div> : null}
            <button type="button" className="btn btn-lg" onClick={() => setExpanded(false)}>
              Close
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
